import React, { useState, useEffect, useCallback } from 'react';
import { CalendarCheck, Clock, LogIn, LogOut, AlertCircle, RefreshCw } from 'lucide-react';
import { api } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import DateInput from '../../components/common/DateInput';

const MONTHS = ['','Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const statusBadge = {
  present:   'bg-green-100 text-green-700',
  absent:    'bg-red-100 text-red-700',
  on_leave:  'bg-blue-100 text-blue-700',
  half_day:  'bg-yellow-100 text-yellow-700',
};

export default function TeacherAttendance() {
  const { currentUser } = useAuth();
  const isTeacher = currentUser?.role === 'teacher';
  const myStaffId = currentUser?.profileId || '';

  const now = new Date();
  const [records,      setRecords]    = useState([]);
  const [staff,        setStaff]      = useState([]);
  const [leaveTypes,   setLeaveTypes] = useState([]);
  const [loading,      setLoading]    = useState(true);
  const [filterStaff,  setFilterStaff]  = useState(isTeacher ? myStaffId : '');
  const [filterMonth,  setFilterMonth]  = useState(String(now.getMonth()+1));
  const [filterYear,   setFilterYear]   = useState(String(now.getFullYear()));
  const [summary,      setSummary]    = useState([]);

  // Mark-leave modal
  const [markModal,  setMarkModal]  = useState(false);
  const [markDate,   setMarkDate]   = useState(now.toISOString().split('T')[0]);
  const [markStaff,  setMarkStaff]  = useState('');
  const [markType,   setMarkType]   = useState('');
  const [marking,    setMarking]    = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { month: filterMonth, year: filterYear };
      if (filterStaff) params.staffId = filterStaff;
      const qs = new URLSearchParams(params).toString();
      const token = JSON.parse(localStorage.getItem('sms_auth') || '{}')?.token || '';
      const [rec, st, lt, sum] = await Promise.all([
        fetch(`/api/teacher-checkins?${qs}`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
        api.list('staff'),
        api.getLeaveTypes(),
        api.getCheckinSummary(filterMonth, filterYear),
      ]);
      setRecords(rec || []);
      setStaff(st.filter(s => s.status === 'active'));
      setLeaveTypes(lt);
      setSummary(sum || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [filterStaff, filterMonth, filterYear]);

  useEffect(() => { load(); }, [load]);

  const doMarkLeave = async () => {
    if (!markStaff || !markDate) { alert('Select staff and date'); return; }
    setMarking(true);
    try {
      await api.markStaffLeave({ staffId: markStaff, date: markDate, leaveTypeId: markType || undefined });
      setMarkModal(false);
      await load();
    } catch (e) { alert('Error: ' + e.message); }
    finally { setMarking(false); }
  };

  const fmtTime = (dt) => {
    if (!dt) return '—';
    try {
      const d = new Date(dt);
      const h = d.getHours() % 12 || 12;
      const m = String(d.getMinutes()).padStart(2,'0');
      return `${h}:${m} ${d.getHours()>=12?'PM':'AM'}`;
    } catch { return dt; }
  };

  // Group by staff for summary view
  const visible = filterStaff
    ? records.filter(r => r.staffId === filterStaff)
    : records;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">{isTeacher ? 'My Attendance' : 'Teacher Attendance'}</h1>
          <p className="text-gray-500 text-sm mt-0.5">{isTeacher ? 'Your check-in/out records and leave history' : 'Check-in/out records, late arrivals, and leave tracking'}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="flex items-center gap-1.5 px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">
            <RefreshCw size={13} /> Refresh
          </button>
          {!isTeacher && (
            <button onClick={() => setMarkModal(true)}
              className="flex items-center gap-1.5 px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600">
              <CalendarCheck size={14} /> Mark Leave
            </button>
          )}
        </div>
      </div>

      {/* Mark Leave Modal */}
      {markModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
            <h2 className="text-lg font-bold mb-4">Mark Staff on Leave</h2>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Staff Member</label>
                <select value={markStaff} onChange={e => setMarkStaff(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300">
                  <option value="">Select staff…</option>
                  {staff.map(s => <option key={s.id} value={s.id}>{s.firstName} {s.lastName}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Date</label>
                <DateInput value={markDate} onChange={e => setMarkDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Leave Type (optional)</label>
                <select value={markType} onChange={e => setMarkType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300">
                  <option value="">None</option>
                  {leaveTypes.map(lt => <option key={lt.id} value={lt.id}>{lt.code} — {lt.name}</option>)}
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setMarkModal(false)} className="px-4 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
              <button onClick={doMarkLeave} disabled={marking}
                className="px-5 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 disabled:opacity-50">
                {marking ? 'Marking…' : 'Mark Leave'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-3 mb-5 flex-wrap">
        {!isTeacher && (
          <select value={filterStaff} onChange={e => setFilterStaff(e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300 min-w-48">
            <option value="">All Staff</option>
            {staff.map(s => <option key={s.id} value={s.id}>{s.firstName} {s.lastName}</option>)}
          </select>
        )}
        <select value={filterMonth} onChange={e => setFilterMonth(e.target.value)}
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300">
          {MONTHS.slice(1).map((m,i) => <option key={i+1} value={i+1}>{m}</option>)}
        </select>
        <select value={filterYear} onChange={e => setFilterYear(e.target.value)}
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300">
          {[now.getFullYear()-1, now.getFullYear()].map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      {/* Monthly summary cards */}
      {summary.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
          {summary.map(s => (
            <div key={s.staffId} className="bg-white rounded-xl border border-gray-100 shadow-sm px-4 py-3">
              <p className="font-semibold text-gray-800 text-sm">{s.firstName} {s.lastName}</p>
              <p className="text-xs text-gray-400 mb-2">{s.designation}</p>
              <div className="grid grid-cols-4 gap-1 text-center">
                <div><p className="text-sm font-bold text-green-600">{s.present}</p><p className="text-xs text-gray-400">Present</p></div>
                <div><p className="text-sm font-bold text-blue-500">{s.onLeave}</p><p className="text-xs text-gray-400">Leave</p></div>
                <div><p className={`text-sm font-bold ${Number(s.lateCount)>0?'text-orange-500':'text-gray-400'}`}>{s.lateCount}</p><p className="text-xs text-gray-400">Late</p></div>
                <div><p className={`text-sm font-bold ${Number(s.halfDayLopsFromLate)>0?'text-red-500':'text-gray-400'}`}>{s.halfDayLopsFromLate}</p><p className="text-xs text-gray-400">½ Cuts</p></div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Records table */}
      {loading ? (
        <div className="text-center py-16 text-gray-400">Loading…</div>
      ) : visible.length === 0 ? (
        <div className="text-center py-12 text-gray-400">No attendance records for this period</div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                {['Date','Staff','Status','Check In','Check Out','Late','Method'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {visible.slice(0,200).map(r => {
                const s = staff.find(st => st.id === r.staffId);
                return (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-700">{r.date ? r.date.split('T')[0] : '—'}</td>
                    <td className="px-4 py-3">
                      {s ? `${s.firstName} ${s.lastName}` : r.staffId}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${statusBadge[r.status]||'bg-gray-100 text-gray-500'}`}>
                        {r.status?.replace('_',' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600 flex items-center gap-1">
                      {r.checkIn ? <><LogIn size={12} className="text-green-500" />{fmtTime(r.checkIn)}</> : '—'}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {r.checkOut ? <span className="flex items-center gap-1"><LogOut size={12} className="text-red-400" />{fmtTime(r.checkOut)}</span> : '—'}
                    </td>
                    <td className="px-4 py-3">
                      {r.isLate ? (
                        <span className="flex items-center gap-1 text-orange-500 font-medium text-xs">
                          <AlertCircle size={12} /> {r.lateMinutes}m late
                        </span>
                      ) : <span className="text-xs text-gray-400">On time</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-gray-400 capitalize">{r.method || 'frs'}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
